import React from "react";

import { css, cx } from "@emotion/css";
import { SketchPicker } from "react-color";

import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme, useTheme } from "@/theme/CreateTheme.ts";
import { ToggleButton } from "@/uiKit/atoms/ToggleButton.tsx";
import { TooltipMenu } from "@/uiKit/atoms/TooltipMenu.tsx";
import { AtomProps } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { toggleButtonStyles } from "@/uiKit/molecules/RichText/inputs/RichTextInputTypes.ts";

export type BaseTextColorPickerButtonProps = {
  /** Id of the color picker popup. */
  popupId?: string;
  color: string;
  onSelectColor: (color: string) => void;
};

export type TextColorPickerButtonProps = AtomProps<BaseTextColorPickerButtonProps>;

export const TextColorPickerButton: React.FC<TextColorPickerButtonProps> = ({
  popupId,
  color,
  onSelectColor,
  className,
  ...props
}) => {
  const styles = useStyles(makeStyles, color);
  const theme = useTheme();

  return (
    <TooltipMenu
      popupId={popupId}
      className={cx(styles.container, className)}
      placement="right-end"
      wrapperClassName={styles.container}
      animation={{
        duration: 0,
      }}
      content={
        <SketchPicker
          color={color ?? theme.palette.common.white}
          onChangeComplete={color => onSelectColor(color.hex)}
        />
      }
      closeOnInteract={false}
      click={{ enabled: true }}
      openOnHover={false}
    >
      <ToggleButton
        data-action="common.rich-text.open-color"
        value="text-color"
        aria-label="color"
        toggled={false}
        onToggle={() => {}}
        {...props}
      >
        <Icons.colorSelect className={cx(toggleButtonStyles.icon, styles.selectedColor)} />
      </ToggleButton>
    </TooltipMenu>
  );
};

const makeStyles = (_: Theme, color: string) => ({
  container: css({
    display: "flex",
  }),
  selectedColor: css({
    "> .selected-color": {
      fill: color,
    },
  }),
});
